<template>
  <div class="cs-table-wrapper">
    <ag-grid-vue v-if="row_data !== undefined" class="ag-theme-alpine" :columnDefs="column_defs"
    :defaultColDef="default_cols" :rowData="row_data"
    :style="`height: ${grid_height}px; width: auto; margin-top: 20px;`" rowHeight="50"
    :alwaysShowHorizontalScroll="false" :pagination="true" :paginationPageSize="16" :gridOptions="gridOptions"
    @grid-ready="onGridReady" id="cs_table"/>
    <div class="cs-statement-modal" v-show="show_statement" @click="show_statement = false" id="statement-wrapper">
      {{current_statement}}<br>
      <span style="margin: auto; font-weight: 600;">Click on box to close</span>
    </div>
  </div>
</template>

<script setup lang="js">
import { AgGridVue } from 'ag-grid-vue3';
import { onMounted, ref, computed, watch, onUnmounted } from 'vue';
import { useStore } from 'vuex';
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import agg from '@/methods/ag_grid_toolbox';
import TooltipHeader from '@/components/tables/TooltipHeader.vue';


const store = useStore();
const column_defs = computed(() => store.state.ag_grid_coldefs[props?.table_id]);
const row_data = computed(() => store.state.ag_grid_rowdata[props?.table_id]);
const row_keys = computed(() => store.state.ag_grid_rowkeys[props?.table_id]);

const default_cols = ref()
/* Grid height is set to 16*50 + 100 for 16 rows*/
const grid_height = computed(() => {
  var temp_height = Math.min(900, (row_keys.value.length + 2) * 50);
  if (props?.maxHeight) temp_height = Math.min(props?.maxHeight, temp_height);
  return temp_height;
})

const gridOptions = {};

/**
 * @prop maxHeight - Can be passed to limit this table's maximum height (in px).
 * @prop table_id - ID for the table. Important for HTML query selections, so it should be unique.
 */
const props = defineProps({
  maxHeight: Number,
  table_id: String,
})

/**
 * When this table is loaded, the following steps are performed:
 * 1. Default column definitions, set in ag_grid_toolbox.js, are used.
 * 2. Specific column definitions for treatments are created.
 * 3. Variant treatment data is turned into an AG Grid RowData object.
 * 4. Columns are resized and the page is set to 0.
 * 5. Event listeners for sorting and resizing are added.
 */
onMounted(() => {
  default_cols.value = agg.createDefaultColDefs();
  csToRowData();
  createColDefs();
  gridOptions.api?.sizeColumnsToFit();
  gridOptions.api?.paginationGoToPage(0);
  window.addEventListener('resize', resizeGrid);
  window.addEventListener('sort-grid', (e) => onSort(e))
});

/**
 * Event listeners for sorting and resizing are removed on unmounting the component.
 */
onUnmounted(() => {
  window.removeEventListener('resize', resizeGrid);
  window.removeEventListener('sort-grid', (e) => onSort(e))
})

/**
 * Called when the sort-grid window event is caught. Applies a specific sort to the grid and refreshes it.
 * Will sort based on a targeted column based on a provided sorting method (asc, desc, none)
 * @param {CustomEvent} e - Event that contains sorting information (column, desired order)
 */
const onSort = (e) => {
  gridOptions.columnApi?.applyColumnState({
    state: [{colId: e.detail.column.toLowerCase(), sort: e.detail.order}],
    defaultState: {sort: null},
  });
  gridOptions.api?.refreshCells();
}

/**
 * Sorts the table.
 * @param {String} column - The column to sort by (preferably ID, but title should also work)
 * @param {String} order - one of 'asc', 'desc' or 'none'
 */
const sortTableBy = (column, order) => {
  console.log(`sorting ${column} and ${order}`)
  gridOptions.columnApi?.applyColumnState({
    state: [{ colId: column.toLowerCase(), sort: order }],
    defaultState: { sort: null },
  });
}

/**
 * Once the grid is ready, it is refreshed and resized.
 */
const onGridReady = () => {
  gridOptions.api?.refreshCells();
  sortTableBy('score', 'desc');
  resizeGrid();
}

/**
 * Resizes/re-renders specific elements of the grid based on the screen width.
 * Sizes should be set in accordance with any media queries.
 */
const resizeGrid = () => {
  gridOptions.api?.sizeColumnsToFit();
  let current_coldefs = gridOptions.api?.getColumnDefs();
  if (!current_coldefs) return;

  // Sizes below 1300
  if (window.innerWidth < 1300) {
    current_coldefs.forEach((def, _) => {
      def.headerComponent = null;
      def.headerName = shortened_headers[def.colId];
      if (def.colId == 'match_type')
        def.cellStyle = {...def.cellStyle, wordBreak: 'break-all'};

      // Sizes below 1000
      if (window.innerWidth < 1000) {
        if (def.colId == 'citation_details') {
          def.width = '60';
          def.maxWidth = '60';
          def.minWidth = '60';
          def.cellRenderer = (params) => addInfoButton(params?.data.evidence_statement, "small")
        }
      }
      // Sizes between 1000 and 1300
      else {
        if (def.colId == 'citation_details') {
          def.width = '80';
          def.maxWidth = '80';
          def.minWidth = '80';
          def.cellRenderer = (params) => addInfoButton(params?.data.evidence_statement, "small")
        }
      }
    });
  }
  // Normal size (> 1300)
  if (window.innerWidth > 1300) {
    current_coldefs.forEach((def, _) => {
      def.headerComponent = TooltipHeader;
      def.headerName = long_headers[def.colId];
      if (def.colId == 'match_type')
        def.cellStyle = {...def.cellStyle, wordBreak: 'normal'};

      if (def.colId == 'citation_details') {
        def.width = '120';
        def.maxWidth = '120';
        def.minWidth = '120';
        def.cellRenderer = (params) => addInfoButton(params?.data.evidence_statement, "full")
      }
    })
  }
  gridOptions.api?.setColumnDefs(current_coldefs);
  gridOptions.api?.redrawRows();
}

function customComparator(valueA, valueB) {
  // Example: Compare by a custom property within the cell renderer
  if (valueA.customProperty > valueB.customProperty) {
    return 1;
  } else if (valueA.customProperty < valueB.customProperty) {
    return -1;
  }
  return 0;
}

function PercentValueGetter ( params ) {
  //alert(JSON.stringify(params.data.drug_name))
  return params.data.drug_name;
}

function CitationIDValueGetter ( params ) {
  //alert(JSON.stringify(params.data.drug_name))
  return params.data.citation_id;
}

/**
 * Creates table column definitions. These are specific for this table.
 * Saves the definitions in the column_defs ref (see below) and applies them to the grid.
 * If keys and key_values refs are defined below, they are used to automatically create some column definitions.
 */
const createColDefs = () => {
  let new_column_defs = [];

  // Auto-Columns (based on keys/key_headers refs defined below)
  for (let column of key_headers.value) {
    if (column === "Drugs")
    {
          // Drug column
          new_column_defs.push({
            headerName: 'Drugs', colId: 'drugs', headerClass: 'presentation-header-left',
            headerComponent: TooltipHeader, headerComponentParams: {title: 'Drugs'},
            cellRenderer: (params) => addDrugLink(params?.data.drug_name),
            filter:true,
            floatingFilter: true,
            suppressFilterButton: false,
            enableFilterButton: true,
            valueGetter: PercentValueGetter
          });
    } else {
        new_column_defs.push({
          headerName: column, field: keys.value[key_headers.value.indexOf(column)],
          colId: keys.value[key_headers.value.indexOf(column)], sortable: 'true', headerClass: 'presentation-header-left',
          wrapText: true, autoHeight: true, headerComponent: TooltipHeader, headerComponentParams: { title: column, colid: keys.value[key_headers.value.indexOf(column)],
          tooltip_id: keys.value[key_headers.value.indexOf(column)]},
          filter:true,
          floatingFilter: true,
          suppressFilterButton: false,
          enableFilterButton: true,
          cellStyle: { justifyContent: "flex-start", lineHeight: "100%", textAlign: "left" },
        });
    }
  }

  // Citation column
  new_column_defs.push({
    headerName: 'Citation', colId: 'citation', headerClass: 'presentation-header-left',
    headerComponent: TooltipHeader, headerComponentParams: {title: 'Citation'},
    filter:true,
    floatingFilter: true,
    suppressFilterButton: false,
    enableFilterButton: true,
    valueGetter: CitationIDValueGetter,
    cellRenderer: (params) => addExternalLink(params?.data.citation_id, params?.data.citation_url)
  });

  // Info Button
  new_column_defs.push({
    headerName: 'Citation Details', colId: 'citation_details', headerClass: 'presentation-header',
    headerComponent: TooltipHeader, headerComponentParams: {title: 'Details', colid: 'citation_details', tooltip_id: 'evidence_statement'},
    cellRenderer: (params) => addInfoButton(params?.data.evidence_statement, "full"),
    cellStyle: {justifyItems: "flex-center"}, pinned: 'right', flex: 0, maxWidth: 150, minWidth: 150,
  });

  store.dispatch('setAGGColdefs', { defs: new_column_defs, id: props?.table_id });
  gridOptions.api?.setColumnDefs(new_column_defs);
};

function isObject(obj) {
    return obj !== null && typeof obj === 'object' && !Array.isArray(obj);
}

function isDictionary(obj) {
    return isObject(obj) && Object.keys(obj).length > 0;
}

/**
 * Turns clinical significance data into rows for the AG Grid.
 * Uses two refs (see below): row_data and keys.
 */
const csToRowData = () => {

  // Check if data is present
  var biomarker_list = store.getters.getActiveBiomarker;
  var list_to_use = biomarker_list[Object.keys(biomarker_list)[0]].onkopus_aggregator.merged_match_types_data;
  var drug_classes = biomarker_list[Object.keys(biomarker_list)[0]].drug_classes

  if (!list_to_use || list_to_use == {}) return;

  var new_row_data = [];

  for (let bm_entry of list_to_use) {
    var new_row = {};
    /* Row data is created automatically based on the properties defined below */
    for (let feature in bm_entry) {
      if (feature === 'drugs') {
        new_row["drug_name"] = ""
        new_row["drug_class"] = ""
        var c_drug = 0
        var num_drug_classes = 0
        for (let drug in bm_entry['drugs']) {
            if ((c_drug >0) && (bm_entry['drugs'][drug]["drug_name"].localeCompare("") != 0)) {
                new_row["drug_name"] += ", "
            }

            new_row["drug_name"] += bm_entry['drugs'][drug]["drug_name"] //+ "<a>P</a>"

            // iterate drug classes
            if (isDictionary(bm_entry['drugs'][drug])) {
                if ("drug_name" in bm_entry['drugs'][drug]) {
                    if (drug_classes) {
                        if (bm_entry['drugs'][drug]["drug_name"].toLowerCase() in drug_classes) {
                            if (num_drug_classes >0) {
                                new_row["drug_class"] += ", "
                            }
                            var drug_classname = String(drug_classes[bm_entry['drugs'][drug]["drug_name"].toLowerCase()])

                            if (typeof drug_classname !== 'string' || drug_classname.length === 0) {

                            } else {
                                drug_classname = drug_classname.charAt(0).toUpperCase() + drug_classname.slice(1) + "\n"
                            }

                            new_row["drug_class"] += drug_classname
                            num_drug_classes += 1
                        }
                    }
                }
            }
            c_drug += 1
        }

      } else {
        new_row[feature] = bm_entry[feature]
      }
    }
    new_row_data.push(new_row);
  }

  store.dispatch('setAGGRowdata', { data: new_row_data, id: props?.table_id });
  store.dispatch('setAGGRowkeys', { keys: Object.keys(list_to_use), id: props?.table_id });

};

/**
 * Adds a link to an external resource, e.g. a PubMed article. Will open in a new tab.
 * @param {String} id - What will be shown instead of the URL
 * @param {String} url - URL to go to
 */
const addExternalLink = (id, url) => {
  const link = document.createElement("a");
  link.style.textDecoration = "none";
  link.innerText = id;
  link.href = url;
  link.target = "_blank";
  return link;
}

const addDrugLink = (drug_name) => {
  const wrapper = document.createElement("div");

  //alert(JSON.stringify(drugs))
  //wrapper.innerText = drug_name;
  //link.href = url;
  //alert(drug_name.length)
  if (drug_name.length === 0) {
  } else {
      var drug_names = drug_name.split(',')
      for (var drug_index in drug_names) {
          var drug = drug_names[drug_index]
          const link = document.createElement("a");
          link.style.textDecoration = "none";

          const drug_label = document.createElement("span")
          drug_label.innerText = drug
          drug_label.style = "margin-right:5px;"

          const img = document.createElement("img")
          img.src = require("../../assets/pubchem_logo.png")
          img.style="width:15px;"
          link.href="https://pubchem.ncbi.nlm.nih.gov/compound/" + drug
          //link.target = "_blank";
          link.appendChild(drug_label)
          link.appendChild(img)
          wrapper.appendChild(link)
      }
  }
  return wrapper;
}

/**
 * Adds an "info" button that will show an additional evidence statement when clicked.
 * @param {String} statement - Evidence statement, e.g. from a PubMed article 
 * @param {String} size - Either "full" or "small", dictates the size/content of the button
 */
const addInfoButton = (statement, size="full") => {
  const info_button = document.createElement("div");

  info_button.innerText = "Details";

  if (statement && statement != '') {
    info_button.classList.add("details-button");
  } else {
    info_button.classList.add("details-button-disabled");
  }

  info_button.classList.add("text-button");
  if (size != "full") {
    info_button.innerText = 'D.'
    info_button.classList.add("details-button-small")
  }
  info_button.addEventListener("click", e => {
    e.preventDefault();
    var top = Math.min(e.pageY, document.body.scrollHeight - 400);
    showEvidence(statement, top);
  });
  return info_button;
}

const addPubmedButton = (statement, size="full") => {
  const info_button = document.createElement("a");
  info_button.href = "https://pubchem.ncbi.nlm.nih.gov/compound/" + statement
  info_button.innerText = "pubmed"

  return info_button
}

/**
 * Enables visibility of the evidence modal, which shows a statement taken from the PubMed source.
 * @param {String} statement - The evidence statement to show
 * @param {Number} top - px value where the modal will be shown on screen (top coordinate) 
 */
const showEvidence = (statement, top) => {
  document.getElementById("statement-wrapper").style.top = `${top}px`;
  current_statement.value = statement;
  show_statement.value = true;
}

/**
 * Redraws the grid once the loading process stops.
 * Somehow refreshCells() is not enough here, so all rows have to be redrawn.
 */
watch(() => store.getters.getProcessState('load-results'), () => {
  gridOptions.api?.redrawRows();
  gridOptions.api?.sizeColumnsToFit();
});

/* Contains all properties that should be auto-extracted from the biomarker object. */
const keys = ref(['biomarker', 'match_type', 'disease', 'drug_class', 'drug_name',
  'evidence_level_onkopus', 'response', 'score','source', 'citation_url', 'citation_id', 'evidence_statement']);
/* Contains headers for columns that should be auto-created from these properties, in the same order! */
/* keys can also contain values at the end that are not automatically used for columns - */
/* this is useful when columns require cellRenderers etc and have to be set up manually. */
const key_headers = ref(['Found Alteration', 'Match Type', 'Disease', 'Drug Class', 'Drugs',
  'Evidence Level', 'Response Type', 'Level', 'Source']);
const shortened_headers = {
  'biomarker': 'Alt.',
  'match_type': 'MT',
  'disease': 'Dis.',
  'drug_class': 'DC',
  'drug_name': 'Dr.',
  'evidence_level_onkopus': 'EL',
  'response': 'Rs.',
  'score': 'L.',
  'source': 'Src.',
  'citation': 'Cit.',
  'citation_details': 'Dt.'
};
const long_headers = {
  'biomarker': 'Found Alteration',
  'match_type': 'Match Type',
  'disease': 'Disease',
  'drug_class': 'Drug Class',
  'drug_name': 'Drug',
  'evidence_level_onkopus': 'Evidence Level',
  'response': 'Response',
  'score': 'Level',
  'source': 'Source',
  'citation': 'Citation',
  'citation_details': 'Details'
}
const show_statement = ref(false);
const current_statement = ref("");
</script>

<style scoped>

.cs-statement-modal {
  position: absolute;
  top: 50%;
  left: 50%;
  z-index: 1000;
  transform: translate(-50%, -50%);
  display: flex;
  flex-wrap: wrap;
  max-width: 40rem;
  max-height: 400px;
  overflow-y: auto;
  padding: 10px;
  border: var(--dark-grey) solid 2px;
  background-color: var(--light-grey);
  font-size: var(--font-400);
}

</style>